import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import '../../../styles/aida-base.css';
import '../../../styles/aida-responsive.css';

/**
 * AboutLanding Component
 * Hero/landing section for the About page.
 * Desktop: absolute-positioned 1024px canvas (hidden lg:block)
 * Mobile: vertically stacked flow layout (flex lg:hidden)
 * Transparent background — inherits from page background zones.
 */

const WHITE = 'rgba(255, 255, 255, 1)';
const YELLOW = 'rgba(251, 191, 36, 1)';

// Background nebulae
const NEBULA_A =
  'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/4610144/image-0bdd0551-e329-4b7b-baf5-bbc90abfb745.webp?e=webp&cX=106&cY=5&cW=1228&cH=825';
const NEBULA_B =
  'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/4610144/image-0bdd0551-e329-4b7b-baf5-bbc90abfb745.webp?e=webp&cX=0&cY=5&cW=1440&cH=825';
const NEBULA_C =
  'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/4610144/image-0bdd0551-e329-4b7b-baf5-bbc90abfb745.webp?e=webp&cX=0&cY=0&cW=1438&cH=831';

// Background screenshots
const BG_SCREENSHOT_FULL =
  'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5769455/image-13257f5a-bd54-42e8-ad07-df92b2d5d4fa.png?w=1669&e=webp&nll=true';
const BG_SCREENSHOT_CROP_A =
  'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5769455/image-13257f5a-bd54-42e8-ad07-df92b2d5d4fa.png?w=1217&e=webp&nll=true&cX=0&cY=27&cW=1920&cH=1026';
const BG_SCREENSHOT_CROP_B =
  'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5769455/image-13257f5a-bd54-42e8-ad07-df92b2d5d4fa.png?w=816&e=webp&nll=true&cX=381&cY=0&cW=1158&cH=1080';

// Game cards
const GAME_CARD_MAIN = '/img/aida/About/WoA_Laptop_mockup.png';
const GAME_CARD_SECONDARY =
  'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5951509/image-e61bb986-a3c9-425a-ae16-9bf992733d01.png?w=1563&e=webp&nll=true&cX=101&cY=353&cW=5798&cH=3295';

// Game badge
const GAME_BADGE = '/img/aida/About/Fortnite_double_phone.png';

// Character sprites
const CHARACTER_A =
  'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5951509/image-9724e1db-3997-456e-9e88-84e86d7f7b6a.png?w=369&e=webp&nll=true&cX=1250&cY=104&cW=668&cH=776';
const CHARACTER_B =
  'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5951509/image-9724e1db-3997-456e-9e88-84e86d7f7b6a.png?w=323&e=webp&nll=true&cX=123&cY=207&cW=558&cH=642';

// UI elements
const UI_CONTROLLER =
  'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5951509/image-64499c5b-d7df-48ab-8bd4-886cedfea5a2.png?w=351&e=webp&nll=true&cX=0&cY=2725&cW=1477&cH=1082';
const UI_GAME_ICON =
  'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5951509/image-64499c5b-d7df-48ab-8bd4-886cedfea5a2.png?w=299&e=webp&nll=true&cX=2345&cY=15&cW=1261&cH=1246';
const UI_BANNER =
  'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5951509/image-64499c5b-d7df-48ab-8bd4-886cedfea5a2.png?w=396&e=webp&nll=true&cX=4204&cY=324&cW=1676&cH=588';
const UI_SMALL_ICON =
  'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5951509/image-64499c5b-d7df-48ab-8bd4-886cedfea5a2.png?w=139&e=webp&nll=true&cX=4427&cY=949&cW=585&cH=483';
const UI_STAR =
  'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5951509/image-64499c5b-d7df-48ab-8bd4-886cedfea5a2.png?w=118&e=webp&nll=true&cX=418&cY=1542&cW=888&cH=915';
const UI_LOGO_SMALL =
  'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5951509/image-64499c5b-d7df-48ab-8bd4-886cedfea5a2.png?w=132&e=webp&nll=true&cX=3962&cY=2364&cW=1450&cH=763';
const UI_LOGO_LARGE =
  'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5951509/image-64499c5b-d7df-48ab-8bd4-886cedfea5a2.png?w=344&e=webp&nll=true&cX=3962&cY=2350&cW=1450&cH=791';

export const AboutLanding = () => {
  const desktopRef = useRef(null);
  const mobileRef = useRef(null);
  const isInView = useInView(desktopRef, { once: true, margin: '-100px' });
  const isMobileInView = useInView(mobileRef, { once: true, amount: 0.1 });

  return (
    <>
      {/* ── Desktop Layout ── */}
      <motion.section
        ref={desktopRef}
        className="relative mx-auto hidden w-full lg:block"
        style={{
          width: '100%',
          height: '520px',
          backgroundColor: 'transparent',
          overflow: 'visible',
          position: 'relative',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <motion.div
          style={{ position: 'relative', width: '100%', height: '100%' }}
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          {/* ── Background Nebulae ── */}
          <div style={{ position: 'absolute', left: '-190px', top: '81px', width: '623px', height: '418px', zIndex: 355, transform: 'rotate(77deg)', pointerEvents: 'none' }}>
            <img src={NEBULA_A} alt="" style={{ opacity: 0.27 }} draggable={false} />
          </div>
          <div style={{ position: 'absolute', left: '-82px', top: '149px', width: '590px', height: '338px', zIndex: 357, transform: 'rotate(-99deg)', pointerEvents: 'none' }}>
            <img src={NEBULA_B} alt="" style={{ opacity: 0.27 }} draggable={false} />
          </div>
          <div style={{ position: 'absolute', left: '727px', top: '654px', width: '605px', height: '350px', zIndex: 324, transform: 'rotate(-92deg) scaleX(-1)', pointerEvents: 'none' }}>
            <img src={NEBULA_C} alt="" style={{ opacity: 0.43 }} draggable={false} />
          </div>

          {/* ── Background Screenshots ── */}
          <img src={BG_SCREENSHOT_FULL} alt="" style={{ position: 'absolute', left: '-199px', top: '129px', width: '480px', height: '270px', zIndex: 358, transform: 'rotate(-40deg) scaleX(-1)', opacity: 0.47, pointerEvents: 'none' }} draggable={false} />
          <img src={BG_SCREENSHOT_FULL} alt="" style={{ position: 'absolute', left: '718px', top: '130px', width: '480px', height: '270px', zIndex: 359, transform: 'rotate(30deg)', opacity: 0.47, pointerEvents: 'none' }} draggable={false} />
          <img src={BG_SCREENSHOT_CROP_A} alt="" style={{ position: 'absolute', left: '-159px', top: '263px', width: '350px', height: '187px', zIndex: 361, transform: 'rotate(272deg) scaleX(-1) scaleY(-1)', opacity: 0.47, pointerEvents: 'none' }} draggable={false} />
          <img src={BG_SCREENSHOT_CROP_B} alt="" style={{ position: 'absolute', left: '885px', top: '241px', width: '235px', height: '219px', zIndex: 360, transform: 'rotate(92deg) scaleY(-1)', opacity: 0.47, pointerEvents: 'none' }} draggable={false} />

          {/* ── Title ── */}
          <div style={{ position: 'absolute', left: '330px', top: '78px', width: '386px', zIndex: 382 }}>
            <h1 style={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 900,
              fontSize: '50px',
              lineHeight: '50px',
              color: WHITE,
              margin: 0,
              fontVariationSettings: "'wght' 900",
            }}>
              More <span style={{ color: YELLOW }}>Games,</span> Fewer Barriers
            </h1>
          </div>

          {/* ── Subtitle ── */}
          <div style={{ position: 'absolute', left: '330px', top: '181px', width: '353px', zIndex: 384 }}>
            <p style={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize: '10px',
              lineHeight: '13px',
              color: WHITE,
              margin: 0,
            }}>
              Building tools for games to be played: any place, any time.
            </p>
          </div>

          {/* ── Game Cards (positions extracted from animation containers) ── */}
          <img src={GAME_CARD_MAIN} alt="" style={{ position: 'absolute', left: '137px', top: '182px', width: '480px', height: '320px', zIndex: 371, transform: 'rotate(9deg)' }} draggable={false} />
          <img src={GAME_CARD_SECONDARY} alt="" style={{ position: 'absolute', left: '360px', top: '229px', width: '450px', height: '255px', zIndex: 370, transform: 'rotate(-22deg)' }} draggable={false} />

          {/* ── Game Badge ── */}
          <img src={GAME_BADGE} alt="" style={{ position: 'absolute', left: '392px', top: '211px', width: '389px', height: 'auto', zIndex: 373, transform: 'rotate(-82deg)' }} draggable={false} />

          {/* ── Characters ── */}
          <img src={CHARACTER_A} alt="" style={{ position: 'absolute', left: '244px', top: '291px', width: '106px', height: '123px', zIndex: 380, transform: 'rotate(-15deg) scaleX(-1)', filter: 'drop-shadow(rgba(3, 10, 42, 0.2) -2px 3px 4px)' }} draggable={false} />
          <img src={CHARACTER_B} alt="" style={{ position: 'absolute', left: '607px', top: '381px', width: '93px', height: '107px', zIndex: 374 }} draggable={false} />

          {/* ── Static UI Elements ── */}
          <img src={UI_STAR} alt="" style={{ position: 'absolute', left: '488px', top: '325px', width: '34px', height: '35px', zIndex: 375, transform: 'rotate(7deg)' }} draggable={false} />
          <img src={UI_LOGO_SMALL} alt="" style={{ position: 'absolute', left: '426px', top: '461px', width: '38px', height: '20px', zIndex: 381 }} draggable={false} />
          <img src={UI_LOGO_LARGE} alt="" style={{ position: 'absolute', left: '700px', top: '397px', width: '99px', height: '54px', zIndex: 378 }} draggable={false} />

          {/* ── Animated Rocking Elements ── */}
          <motion.div
            style={{ position: 'absolute', left: '395px', top: '410px', width: '101px', height: '74px', zIndex: 372 }}
            animate={{ rotate: [-5, 5, -5], scale: [1, 0.9, 1] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          >
            <img src={UI_CONTROLLER} alt="" style={{ width: '100%', height: '100%' }} draggable={false} />
          </motion.div>

          <motion.div
            style={{ position: 'absolute', left: '565px', top: '181px', width: '86px', height: '85px', zIndex: 376 }}
            animate={{ rotate: [-5, 5, -5], scale: [1, 0.9, 1] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          >
            <img src={UI_GAME_ICON} alt="" style={{ width: '100%', height: '100%' }} draggable={false} />
          </motion.div>

          <motion.div
            style={{ position: 'absolute', left: '663px', top: '266px', width: '114px', height: '40px', zIndex: 377 }}
            animate={{ rotate: [-5, 5, -5], scale: [1, 0.9, 1] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          >
            <img src={UI_BANNER} alt="" style={{ width: '100%', height: '100%' }} draggable={false} />
          </motion.div>

          <motion.div
            style={{ position: 'absolute', left: '660px', top: '225px', width: '40px', height: '33px', zIndex: 379 }}
            animate={{ rotate: [-5, 5, -5] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          >
            <img src={UI_SMALL_ICON} alt="" style={{ width: '100%', height: '100%' }} draggable={false} />
          </motion.div>
        </motion.div>
      </motion.section>

      {/* ── Mobile Layout — stacked flow ── */}
      <motion.section
        ref={mobileRef}
        className="relative flex w-full flex-col items-center lg:hidden"
        style={{
          width: '100%',
          backgroundColor: 'transparent',
          overflow: 'hidden',
          padding: '80px 20px 0px',
          gap: '16px',
        }}
        initial={{ opacity: 0 }}
        animate={isMobileInView ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Title */}
        <motion.div
          style={{ textAlign: 'center' }}
          initial={{ opacity: 0, y: 20 }}
          animate={isMobileInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          <h1 style={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 900,
            fontSize: '44px',
            lineHeight: '50px',
            color: WHITE,
            margin: 0,
            fontVariationSettings: "'wght' 900",
          }}>
            More <span style={{ color: YELLOW }}>Games,</span>
            <br />
            Fewer Barriers
          </h1>
        </motion.div>

        {/* Subtitle */}
        <p style={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: 400,
          fontSize: '16px',
          lineHeight: '16px',
          color: WHITE,
          textAlign: 'center',
          maxWidth: '280px',
          margin: 0,
        }}>
          Building tools for games to be played: any place, any time.
        </p>

        {/* Image cluster — central elements only, scaled to 0.725, x-offset so GAME_CARD_MAIN starts at x=0 */}
        <div style={{ position: 'relative', width: '490px', height: '240px', flexShrink: 0, transform: 'translateX(-20px)' }}>

          {/* Game Cards */}
          <img src={GAME_CARD_MAIN} alt="" style={{ position: 'absolute', left: '0px', top: '2px', width: '348px', height: '233px', zIndex: 371, transform: 'rotate(9deg)' }} draggable={false} />
          <img src={GAME_CARD_SECONDARY} alt="" style={{ position: 'absolute', left: '162px', top: '37px', width: '326px', height: '185px', zIndex: 370, transform: 'rotate(-22deg)' }} draggable={false} />

          {/* Game Badge */}
          <img src={GAME_BADGE} alt="" style={{ position: 'absolute', left: '185px', top: '23px', width: '282px', height: 'auto', zIndex: 373, transform: 'rotate(-82deg)' }} draggable={false} />

          {/* Characters */}
          <img src={CHARACTER_A} alt="" style={{ position: 'absolute', left: '78px', top: '82px', width: '77px', height: '89px', zIndex: 380, transform: 'rotate(-15deg) scaleX(-1)', filter: 'drop-shadow(rgba(3, 10, 42, 0.2) -2px 3px 4px)' }} draggable={false} />
          <img src={CHARACTER_B} alt="" style={{ position: 'absolute', left: '341px', top: '146px', width: '68px', height: '78px', zIndex: 374 }} draggable={false} />

          {/* Static UI Elements */}
          <img src={UI_STAR} alt="" style={{ position: 'absolute', left: '255px', top: '106px', width: '24px', height: '26px', zIndex: 375, transform: 'rotate(7deg)' }} draggable={false} />
          <img src={UI_LOGO_SMALL} alt="" style={{ position: 'absolute', left: '210px', top: '205px', width: '27px', height: '15px', zIndex: 381 }} draggable={false} />
          <img src={UI_LOGO_LARGE} alt="" style={{ position: 'absolute', left: '408px', top: '158px', width: '72px', height: '39px', zIndex: 378 }} draggable={false} />

          {/* Animated Rocking Elements */}
          <motion.div
            style={{ position: 'absolute', left: '188px', top: '167px', width: '74px', height: '54px', zIndex: 372 }}
            animate={{ rotate: [-5, 5, -5], scale: [1, 0.9, 1] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          >
            <img src={UI_CONTROLLER} alt="" style={{ width: '100%', height: '100%' }} draggable={false} />
          </motion.div>

          <motion.div
            style={{ position: 'absolute', left: '311px', top: '1px', width: '63px', height: '62px', zIndex: 376 }}
            animate={{ rotate: [-5, 5, -5], scale: [1, 0.9, 1] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          >
            <img src={UI_GAME_ICON} alt="" style={{ width: '100%', height: '100%' }} draggable={false} />
          </motion.div>

          <motion.div
            style={{ position: 'absolute', left: '381px', top: '62px', width: '83px', height: '29px', zIndex: 377 }}
            animate={{ rotate: [-5, 5, -5], scale: [1, 0.9, 1] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          >
            <img src={UI_BANNER} alt="" style={{ width: '100%', height: '100%' }} draggable={false} />
          </motion.div>

          <motion.div
            style={{ position: 'absolute', left: '380px', top: '34px', width: '29px', height: '24px', zIndex: 379 }}
            animate={{ rotate: [-5, 5, -5] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          >
            <img src={UI_SMALL_ICON} alt="" style={{ width: '100%', height: '100%' }} draggable={false} />
          </motion.div>
        </div>
      </motion.section>
    </>
  );
};
